
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, Lock, Settings, ArrowRight } from 'lucide-react';
import { usePageAccess } from '@/hooks/usePageAccess';
import { useNavigate } from 'react-router-dom';

const PageAccessCard = () => {
  const { pageAccess, loading } = usePageAccess();
  const navigate = useNavigate();

  const getLockedPages = () => {
    return Object.entries(pageAccess)
      .filter(([_, isLocked]) => isLocked)
      .map(([pageName]) => pageName);
  };

  const getAvailablePages = () => {
    return Object.entries(pageAccess)
      .filter(([_, isLocked]) => !isLocked)
      .map(([pageName]) => pageName);
  };

  const getLockedCount = () => {
    return Object.values(pageAccess).filter(Boolean).length;
  };

  const getTotalPages = () => {
    return Object.keys(pageAccess).length;
  };

  const getAvailableCount = () => {
    return getTotalPages() - getLockedCount();
  };

  const navigateToManagement = () => {
    navigate('/admin/management');
    // TODO: We could add a URL parameter or state to auto-select the page-access section
  };

  if (loading) {
    return (
      <Card className="cursor-pointer hover:shadow-md transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const lockedPages = getLockedPages();
  const lockedCount = getLockedCount();
  const totalPages = getTotalPages();
  const availableCount = getAvailableCount();

  return (
    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={navigateToManagement}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5" />
            Page Access Control
          </CardTitle>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>
        <CardDescription>Manage section accessibility</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Status Overview */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Status Overview</span>
            <div className="flex items-center gap-2">
              <Badge 
                variant="secondary" 
                className="flex items-center gap-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              >
                {availableCount} Available
              </Badge>
              {lockedCount > 0 && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  {lockedCount} Locked
                </Badge>
              )}
            </div>
          </div>

          {/* Locked Pages List */}
          {lockedCount > 0 ? (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Currently Locked:</p>
              <div className="flex flex-wrap gap-1">
                {lockedPages.map((pageName) => (
                  <Badge key={pageName} variant="outline" className="text-xs">
                    {pageName}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">All sections are currently available</p>
          )}

          {/* Quick Action */}
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full mt-3"
            onClick={(e) => {
              e.stopPropagation();
              navigateToManagement();
            }}
          >
            <Settings className="h-4 w-4 mr-2" />
            Manage Access
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PageAccessCard;
